// Promos de un accesorio ya en la venta: las que su alcance nombra (con o sin monto en evaluate) y,
// sin alcance conocido, las que bajan alguna de sus piezas. Puro, sin React.
import type { CatalogItem, ReservedLine } from '@/domains/sales/wizard/types';
import type {
    EvaluatedSalePromotion,
    EvaluateSalePromotionsResponse,
    StoreSalePromotionItem,
} from '../api/wizardPromotionsContract';
import { targetsExtra } from './extrasBenefits';
import { forAnchor, paymentFieldsOf } from './promotionAppliesTo';
import { discountLabel, otherLevers } from './saleBenefits';

/** Promo de la tienda que la evaluación no trajo (sin monto en esta venta), con lo que se sabe de ella. */
export function asEvaluated(promo: StoreSalePromotionItem): EvaluatedSalePromotion {
    const levers = promo.levers;
    return {
        promotionId: promo.promotionId,
        version: promo.version,
        kind: 'SALE',
        name: promo.name,
        customerText: promo.customerText,
        levers: [
            ...(levers.deviceDiscount ? (['DEVICE_DISCOUNT'] as const) : []),
            ...(levers.accessoryDiscount ? (['ACCESSORY_DISCOUNT'] as const) : []),
            ...(levers.gift ? (['GIFT'] as const) : []),
            ...(levers.coupon ? (['COUPON'] as const) : []),
            ...(levers.airtime ? (['AIRTIME'] as const) : []),
            ...(levers.extendedWarranty ? (['EXTENDED_WARRANTY'] as const) : []),
            ...(levers.walletBonus ? (['WALLET_BONUS'] as const) : []),
        ],
        lines: [],
        totalDiscountCents: 0,
        giftOptions: [],
        giftChooseCount: null,
        afterSale: [],
        blockedBy: promo.blockedBy ?? null,
        ...paymentFieldsOf(promo),
        stackable: promo.stackable,
        anchorBound: promo.anchorBound,
        ...(promo.conditions && { conditions: promo.conditions }),
    };
}

/** Un accesorio de la venta: sus líneas y las promos que le tocan. */
export interface AccessoryPromotions {
    sku: string;
    lineIds: string[];
    qty: number;
    promos: EvaluatedSalePromotion[];
}

export function accessoryPromotionsFor(
    sku: string,
    lines: readonly ReservedLine[],
    evaluation: EvaluateSalePromotionsResponse,
    store: readonly StoreSalePromotionItem[],
    catalog: { category?: CatalogItem['category']; tier: string } | undefined,
    withoutSureKeep: boolean,
): AccessoryPromotions {
    const own = lines.filter((l) => l.sku === sku);
    const lineIds = own.map((l) => l.lineId);
    const first = own.at(0);
    const extra = {
        sku,
        category: first?.product?.category ?? catalog?.category,
        tier: first?.product?.tier ?? catalog?.tier ?? '',
    };
    const evaluated = new Map(evaluation.promotions.map((p) => [p.promotionId, p]));
    const scoped = store.filter((p) => targetsExtra(extra, p));
    const scopedIds = new Set(scoped.map((p) => p.promotionId));
    const promos = forAnchor(
        [
            ...scoped.map((p) => evaluated.get(p.promotionId) ?? asEvaluated(p)),
            ...evaluation.promotions.filter(
                (p) =>
                    !scopedIds.has(p.promotionId) &&
                    !p.anchorBound &&
                    p.lines.some((l) => lineIds.includes(l.lineId)),
            ),
        ],
        withoutSureKeep,
    );
    return { sku, lineIds, qty: own.reduce((n, l) => n + l.qty, 0), promos };
}

/** Lo que da, corto: «Cupón $100», «20 % en accesorios»; sin dato, el nombre. */
export function shortPromotionLabel(
    promo: EvaluatedSalePromotion,
    store: readonly StoreSalePromotionItem[],
): string {
    const levers = store.find((p) => p.promotionId === promo.promotionId)?.levers;
    if (!levers) return promo.name;
    if (levers.accessoryDiscount) {
        return `${discountLabel(levers.accessoryDiscount).replace('−', '')} en accesorios`;
    }
    return otherLevers(levers).at(0)?.label ?? promo.name;
}
